import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { seoArticles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL;

function buildArticleJsonLd(article: typeof seoArticles[0], slug: string) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": article.title,
        "description": "A comprehensive resource for Vigorbuy Spreadsheet users. Explore detailed guides, safety tips, category comparisons, and expert shopping strategies.",
        "image": article.heroImageUrl,
        "author": {
          "@type": "Organization",
          "name": "Vigorbuy Editorial Team"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Vigorbuy Spreadsheet",
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg"
          }
        },
        "datePublished": "2026-08-01",
        "dateModified": "2026-08-02",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${siteUrl}/article/${slug}`
        },
        "inLanguage": "en",
        "isAccessibleForFree": true
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": siteUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": article.title,
            "item": `${siteUrl}/article/${slug}`
          }
        ]
      }
    ]
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  const article = seoArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main>
        <header className="w-full bg-background-50 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <span className="font-heading text-xl font-semibold text-foreground-900 tracking-tight">
                  Vigorbuy<span className="text-primary-500">Spreadsheet</span>
                </span>
              </Link>
              <Link to="/" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-left-line mr-1"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </header>
        <div className="w-full bg-background-50 flex items-center justify-center py-32">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-semibold text-foreground-900 mb-3">Article Not Found</h1>
            <p className="text-sm text-foreground-500 mb-6">The article you are looking for does not exist.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
              Back to Homepage
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const articleJsonLd = buildArticleJsonLd(article, slug);
  const tagBgClass = article.tagColor === 'accent' ? 'bg-accent-100 text-accent-700' : article.tagColor === 'secondary' ? 'bg-secondary-100 text-secondary-700' : 'bg-primary-100 text-primary-700';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <main>
        <header className="w-full bg-background-50 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <span className="font-heading text-xl font-semibold text-foreground-900 tracking-tight">
                  Vigorbuy<span className="text-primary-500">Spreadsheet</span>
                </span>
              </Link>
              <div className="flex items-center gap-3 md:gap-4">
                <Link to="/" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">
                  <i className="ri-arrow-left-line mr-1"></i>
                  Back to Home
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="hidden sm:inline-flex items-center px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </header>

        <article className="w-full bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <nav className="text-xs text-foreground-400 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-600">{article.title}</span>
            </nav>

            <header className="mb-10">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 ${tagBgClass}`}>
                {article.tag}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                A comprehensive resource for Vigorbuy Spreadsheet users. Explore detailed guides, safety tips, category comparisons, and expert shopping strategies.
              </p>
              <div className="flex items-center gap-3 mt-6 pt-6 border-t border-background-200">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">Vigorbuy Editorial Team</p>
                  <p className="text-xs text-foreground-400">Updated August 2026 · {article.readTime}</p>
                </div>
              </div>
            </header>

            <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.heroImageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top rounded-lg"
                title={article.title}
              />
            </div>

            <div className="prose-custom">
              {article.sections.map((section, sIdx) => {
                if (section.type === 'heading') {
                  return (
                    <section key={sIdx} className="mb-10">
                      <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                        {section.content}
                      </h2>
                    </section>
                  );
                }

                if (section.type === 'paragraph') {
                  return (
                    <section key={sIdx} className="mb-10">
                      <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                        {section.content}
                      </p>
                    </section>
                  );
                }

                if (section.type === 'list' && section.items) {
                  const ListTag = section.ordered ? 'ol' : 'ul';
                  return (
                    <section key={sIdx} className="mb-10">
                      <ListTag className={`space-y-3 ${section.ordered ? 'pl-5 list-decimal' : 'pl-5 list-disc'}`}>
                        {section.items.map((item, iIdx) => (
                          <li key={iIdx} className="text-sm md:text-base text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.replace(/<strong>(.*?)<\/strong>/g, '<strong class="text-foreground-900">$1</strong>') }}></li>
                        ))}
                      </ListTag>
                    </section>
                  );
                }

                if (section.type === 'categories' && section.items) {
                  return (
                    <section key={sIdx} className="mb-10">
                      <div className="space-y-4">
                        {section.items.map((item, iIdx) => (
                          <div key={iIdx} className="p-5 rounded-lg bg-background-100 border border-background-200">
                            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                              {item.name}
                            </h3>
                            <p className="text-sm text-foreground-600 leading-relaxed">
                              {item.desc}
                            </p>
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                }

                if (section.type === 'checklist' && section.items) {
                  return (
                    <section key={sIdx} className="mb-10">
                      <div className="space-y-3">
                        {section.items.map((item, iIdx) => (
                          <div key={iIdx} className="flex items-start gap-3">
                            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-sm"></i>
                            </div>
                            <p className="text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.replace(/<strong>(.*?)<\/strong>/g, '<strong class="text-foreground-900">$1</strong>') }}></p>
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                }

                if (section.type === 'faq' && section.items) {
                  return (
                    <section key={sIdx} className="mb-10">
                      <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-5">
                        Frequently Asked Questions
                      </h2>
                      <div className="space-y-4">
                        {section.items.map((faq, fIdx) => (
                          <div key={fIdx} className="p-5 rounded-lg bg-background-100 border border-background-200">
                            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                              {faq.question}
                            </h3>
                            <p className="text-sm text-foreground-600 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                }

                return null;
              })}

              <section className="mb-10 p-6 rounded-lg bg-primary-50 border border-primary-200">
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                  Ready to Start Your Product Research?
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                  Now that you have learned more about Vigorbuy Spreadsheet, the next step is simple. Access the full spreadsheet database and start discovering products the smart way.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-shopping-bag-3-line"></i>
                    Vigorbuy Spreadsheet
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-md bg-background-50 border border-primary-200 text-primary-600 text-sm font-medium hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-home-4-line"></i>
                    Back to Homepage
                  </Link>
                </div>
              </section>

              <footer className="border-t border-background-200 pt-8">
                <p className="text-xs text-foreground-400 mb-3">
                  Looking for more resources? Return to the <Link to="/" className="text-primary-500 hover:text-primary-600 underline">Vigorbuy Spreadsheet homepage</Link> to browse all product categories, explore the safety center, and access the latest guides.
                </p>
                <nav className="flex flex-wrap gap-4 text-xs text-foreground-400">
                  <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
                  <Link to="/article/vigorbuy-spreadsheet-safety-guide" className="hover:text-primary-500 transition-colors">Safety Guide</Link>
                  <Link to="/article/vigorbuy-spreadsheet-category-comparison" className="hover:text-primary-500 transition-colors">Category Comparison</Link>
                  <Link to="/article/vigorbuy-spreadsheet-beginner-tutorial" className="hover:text-primary-500 transition-colors">Beginner Tutorial</Link>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-primary-500 transition-colors">Main Website</a>
                </nav>
              </footer>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}